import React, { forwardRef, HTMLAttributeAnchorTarget } from "react";
import Link from "next/link";
import { Row } from "./Stack";
import { isTvEnv } from "@/utils/common.helpers";

interface ButtonProps {
  href?: string;
  rounded?: boolean;
  icon?: React.ReactNode;
  onClick?: (e: any) => void;
  children?: React.ReactNode;
  className?: string;
  fullWidthChildren?: boolean;
  target?: HTMLAttributeAnchorTarget;
  replace?: boolean;
  variant?:
    | "primary"
    | "secondary"
    | "primary-white"
    | "green"
    | "outlined-white"
    | "transparent"
    | "cartoon-3d"
    | "send";
  dataTestId?: string;
  noBg?: boolean;
  disabled?: boolean;
  /**
   * When true, allows click events to be processed even if the button is disabled.
   * Useful for overriding default disabled behavior.
   */
  forceAllowClick?: boolean;
  transition?: boolean;
}

// TODO: Remove classname from this component
export const Button = forwardRef<
  HTMLAnchorElement | HTMLButtonElement,
  ButtonProps
>(
  (
    {
      href,
      rounded = false,
      icon,
      onClick,
      children,
      className,
      target,
      replace,
      variant = "primary",
      dataTestId,
      noBg = false,
      disabled = false,
      fullWidthChildren = false,
      forceAllowClick = false,
      transition = false,
    }: ButtonProps,
    ref,
  ) => {
    const getClassName = () => {
      // Special handling for "send" variant - it doesn't need default padding/width
      const isSendVariant = variant === "send";

      const baseClasses = [
        "flex justify-center items-center gap-2",
        noBg || isSendVariant ? "" : "p-2.5 w-full",
      ];

      if (!noBg) {
        if (variant === "primary") {
          baseClasses.push("bg-stg-primary-cta");
        } else if (variant === "secondary") {
          baseClasses.push(
            "bg-stg-secondary-cta border-2 border-stg-secondary-cta-border",
          );
        } else if (variant === "primary-white") {
          baseClasses.push("bg-white text-black");
        } else if (variant === "green") {
          baseClasses.push("bg-stg-green text-white");
        } else if (variant === "outlined-white") {
          baseClasses.push("bg-transparent border border-white text-white");
        } else if (variant === "transparent") {
          baseClasses.push("bg-transparent");
        } else if (variant === "cartoon-3d") {
          baseClasses.push("bg-white text-stg-maroon !w-fit py-3 px-8 ");
        } else if (isSendVariant) {
          baseClasses.push(
            "bg-stg-red-1 text-white p-3 flex-shrink-0 transition-all disabled:cursor-not-allowed disabled:opacity-50",
          );
        }
      }

      if (transition) {
        baseClasses.push("transition-all duration-200");
      }

      if (disabled) {
        baseClasses.push(
          "!bg-stg-secondary-cta border-2 border-stg-grey-2 !text-stg-grey-3 !cursor-not-allowed",
        );
      }

      // Special rounded styling for send variant
      if (isSendVariant && rounded) {
        baseClasses.push("rounded-full");
      } else {
        baseClasses.push(rounded ? "rounded-3xl" : "rounded-lg");
      }

      if (className) {
        baseClasses.push(className);
      }

      return baseClasses.filter(Boolean).join(" ");
    };

    const content = (
      <>
        {icon && <div className={`${noBg ? "" : "mr-2"}`}>{icon}</div>}
        {children && (
          <div
            className={`text-center text-base font-bold ${fullWidthChildren ? "w-full" : ""}`}
          >
            {children}
          </div>
        )}
      </>
    );

    if (onClick && !href) {
      return (
        <button
          ref={ref as React.Ref<HTMLButtonElement>}
          onClick={onClick}
          className={getClassName()}
          data-testid={dataTestId}
          disabled={!forceAllowClick && disabled}
        >
          {content}
        </button>
      );
    }

    return (
      <Link
        href={href ?? ""}
        replace={replace ?? false}
        className={getClassName()}
        target={target}
        data-testid={dataTestId}
        ref={ref as React.Ref<HTMLAnchorElement>}
        onClick={(e) => {
          if (!forceAllowClick && disabled) {
            e.preventDefault();
            return;
          }
          onClick?.(e);
        }}
      >
        {content}
      </Link>
    );
  },
);

Button.displayName = "Button";
